import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, Form, Input, InputNumber, Button, TreeSelect, Select, Space, message, Spin, Table, Modal, Switch,
} from 'antd';
import { ArrowLeftOutlined, PlusOutlined } from '@ant-design/icons';
import {
  getCategoryTree,
  getFoodItem,
  createFoodItem,
  updateFoodItem,
  getFoodUnits,
  createFoodUnit,
  FoodCategory,
  FoodUnit,
} from '../../api/foods';

const buildTreeData = (cats: FoodCategory[]): any[] =>
  cats.map((c) => ({
    title: c.name,
    value: c.id,
    children: c.children ? buildTreeData(c.children) : undefined,
  }));

const ItemForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [categories, setCategories] = useState<FoodCategory[]>([]);
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [units, setUnits] = useState<FoodUnit[]>([]);
  const [unitModalVisible, setUnitModalVisible] = useState(false);
  const [unitForm] = Form.useForm();
  const isEdit = !!id;

  useEffect(() => {
    getCategoryTree().then(setCategories);
  }, []);

  useEffect(() => {
    if (id) {
      Promise.all([
        getFoodItem(id).then((item) => form.setFieldsValue(item)),
        getFoodUnits(id).then(setUnits),
      ]).finally(() => setLoading(false));
    }
  }, [id, form]);

  const handleSubmit = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      if (isEdit) {
        await updateFoodItem(id!, values);
        message.success('更新成功');
      } else {
        await createFoodItem(values);
        message.success('创建成功');
      }
      navigate('/foods/items');
    } catch {
      // error handled by interceptor
    } finally {
      setSaving(false);
    }
  };

  const handleAddUnit = async () => {
    try {
      const values = await unitForm.validateFields();
      await createFoodUnit(id!, values);
      message.success('单位添加成功');
      setUnitModalVisible(false);
      unitForm.resetFields();
      const updatedUnits = await getFoodUnits(id!);
      setUnits(updatedUnits);
    } catch {
      // error handled by interceptor
    }
  };

  if (loading) {
    return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  }

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/foods/items')}>返回</Button>
      </Space>
      <Card title={isEdit ? '编辑食物' : '添加食物'}>
        <Form form={form} layout="vertical" style={{ maxWidth: 600 }}>
          <Form.Item label="分类" name="categoryId" rules={[{ required: true, message: '请选择分类' }]}>
            <TreeSelect
              showSearch
              treeData={buildTreeData(categories)}
              placeholder="选择分类"
              treeDefaultExpandAll
              treeNodeFilterProp="title"
            />
          </Form.Item>
          <Form.Item label="名称" name="name" rules={[{ required: true, message: '请输入名称' }]}>
            <Input placeholder="如: 米饭、苹果" />
          </Form.Item>
          <Form.Item label="别名" name="aliases">
            <Select mode="tags" placeholder="输入别名后回车，如: 白米饭、大米饭" />
          </Form.Item>
          <Form.Item label="热量 (kcal/100g)" name="caloriesPer100g" rules={[{ required: true, message: '请输入热量' }]}>
            <InputNumber min={0} precision={2} style={{ width: '100%' }} placeholder="0.00" />
          </Form.Item>
          <Form.Item label="蛋白质 (g/100g)" name="proteinPer100g">
            <InputNumber min={0} precision={2} style={{ width: '100%' }} placeholder="可选" />
          </Form.Item>
          <Form.Item label="脂肪 (g/100g)" name="fatPer100g">
            <InputNumber min={0} precision={2} style={{ width: '100%' }} placeholder="可选" />
          </Form.Item>
          <Form.Item label="碳水化合物 (g/100g)" name="carbsPer100g">
            <InputNumber min={0} precision={2} style={{ width: '100%' }} placeholder="可选" />
          </Form.Item>
          <Form.Item label="来源" name="source">
            <Input placeholder="如: 中国食物成分表" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" onClick={handleSubmit} loading={saving}>
                {isEdit ? '保存' : '创建'}
              </Button>
              <Button onClick={() => navigate('/foods/items')}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
      {isEdit && (
        <Card title="计量单位" style={{ marginTop: 16 }} extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setUnitModalVisible(true)}>
            添加单位
          </Button>
        }>
          <Table
            dataSource={units}
            rowKey="id"
            pagination={false}
            columns={[
              { title: '单位名称', dataIndex: 'unitName' },
              { title: '克数', dataIndex: 'grams', render: (v: number) => v != null ? `${v}g` : '-' },
              { title: '描述', dataIndex: 'description', render: (v: string) => v || '-' },
              { title: '默认', dataIndex: 'isDefault', render: (v: boolean) => v ? <span style={{ color: '#52c41a' }}>✓</span> : '-' },
            ]}
          />
          <Modal
            title="添加单位"
            open={unitModalVisible}
            onOk={handleAddUnit}
            onCancel={() => setUnitModalVisible(false)}
          >
            <Form form={unitForm} layout="vertical">
              <Form.Item label="单位名称" name="unitName" rules={[{ required: true, message: '请输入单位名称' }]}>
                <Input placeholder="如: 碗、杯、勺" />
              </Form.Item>
              <Form.Item label="克数" name="grams" rules={[{ required: true, message: '请输入克数' }]}>
                <InputNumber min={0.1} precision={2} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item label="描述" name="description">
                <Input placeholder="如: 标准碗" />
              </Form.Item>
              <Form.Item label="设为默认" name="isDefault" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Form>
          </Modal>
        </Card>
      )}
    </div>
  );
};

export default ItemForm;
